import { useState, useEffect, useCallback, useRef } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { motion } from "framer-motion"

const LIGHT_SECTION_IDS: string[] = []
const DARK_OVERRIDE_IDS = ['features']

const NAV_SECTIONS = [
  { name: 'Features', id: 'features' },
  { name: 'How It Works', id: 'how-it-works' },
  { name: 'FAQs', id: 'faqs' },
]

const EXPLORE_LINKS = [
  { name: 'Football', href: '/sports/football' },
  { name: 'Basketball', href: '/sports/basketball' },
  { name: 'Padel', href: '/sports/padel' },
  { name: 'Communities', href: '/solutions/communities' },
  { name: 'Academies', href: '/solutions/academies' },
  { name: 'Venues', href: '/solutions/venues' },
]

function ExploreMenu({ linkText, activeLinkText }: { linkText: string; activeLinkText: string }) {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const current = EXPLORE_LINKS.some((item) => location.pathname === item.href)

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
        className={`rounded-full px-3.5 py-1.5 text-[13px] font-medium min-h-[40px] ${current ? activeLinkText : linkText}`}
      >
        Explore
      </button>
      {open ? (
        <div className="absolute left-0 top-[calc(100%+10px)] z-[120] min-w-[220px] rounded-2xl border border-white/10 bg-[#0c0c0c] p-2 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.7)]">
          {EXPLORE_LINKS.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`flex min-h-11 items-center rounded-xl px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9] ${
                location.pathname === item.href ? 'text-white bg-white/10' : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              {item.name}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  )
}

const MobileLoginButton = () => {
  return (
    <Link
      to="/login"
      className="inline-flex items-center rounded-full px-3.5 py-1.5 text-sm font-semibold text-black transition-transform duration-200 hover:scale-[1.03] touch-manipulation min-h-[40px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9]"
      style={{ backgroundColor: '#71cdd9' }}
    >
      Log in
    </Link>
  )
}

function useHeaderOverLightSection() {
  const [isLight, setIsLight] = useState(false)
  useEffect(() => {
    const check = () => {
      const threshold = 100
      let overLight = false
      for (const id of LIGHT_SECTION_IDS) {
        const el = document.getElementById(id)
        if (!el) continue
        const rect = el.getBoundingClientRect()
        if (rect.top <= threshold && rect.bottom > 0) { overLight = true; break }
      }
      let overDarkOverride = false
      for (const id of DARK_OVERRIDE_IDS) {
        const el = document.getElementById(id)
        if (!el) continue
        const rect = el.getBoundingClientRect()
        if (rect.top <= threshold && rect.bottom > 0) { overDarkOverride = true; break }
      }
      setIsLight(overLight && !overDarkOverride)
    }
    check()
    window.addEventListener('scroll', check, { passive: true })
    window.addEventListener('resize', check)
    return () => { window.removeEventListener('scroll', check); window.removeEventListener('resize', check) }
  }, [])
  return isLight
}

const SECTION_TO_NAV: Record<string, string> = {
  'home': 'features',
  'features': 'features',
  'how-it-works': 'how-it-works',
  'faqs': 'faqs',
}

function useHideOnScrollDown() {
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    let lastY = window.scrollY
    let ticking = false

    const update = () => {
      const currentY = window.scrollY

      if (currentY < 50) {
        setHidden(false)
      } else if (currentY > lastY && currentY > 100) {
        setHidden(true)
      } else if (currentY < lastY) {
        setHidden(false)
      }

      lastY = currentY
      ticking = false
    }

    const onScroll = () => {
      if (!ticking) {
        ticking = true
        window.requestAnimationFrame(update)
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return hidden
}

function useActiveSection() {
  const [active, setActive] = useState<string>('features')

  useEffect(() => {
    const detect = () => {
      const scrollY = window.scrollY
      const viewportH = window.innerHeight
      const midPoint = scrollY + viewportH * 0.35

      const sectionIds = ['home', 'features', 'how-it-works', 'faqs']
      let best = 'features'

      for (const id of sectionIds) {
        const el = document.getElementById(id)
        if (!el) continue
        const rect = el.getBoundingClientRect()
        const elTop = scrollY + rect.top
        if (elTop <= midPoint) {
          best = SECTION_TO_NAV[id] || id
        }
      }
      setActive(best)
    }

    detect()
    window.addEventListener('scroll', detect, { passive: true })
    return () => window.removeEventListener('scroll', detect)
  }, [])

  return active
}

export const Header = () => {
  const isOverLightSection = useHeaderOverLightSection()
  const activeSection = useActiveSection()
  const navHidden = useHideOnScrollDown()

  const location = useLocation()
  const navigate = useNavigate()
  const isHomePage = location.pathname === '/'
  const isBookDemo = location.pathname === '/book-demo'

  const scrollToSection = useCallback((id: string) => {
    if (isHomePage) {
      const element = document.getElementById(id)
      if (element) element.scrollIntoView({ behavior: 'smooth' })
    } else {
      navigate('/', { state: { scrollTo: id } })
    }
  }, [isHomePage, navigate])

  const pillBase = isOverLightSection
    ? 'bg-black/[0.04] border-black/[0.08] text-black/70'
    : 'bg-white/[0.05] border-white/[0.1] text-white/80'
  const linkText = isOverLightSection ? 'text-black/60 hover:text-black' : 'text-white/65 hover:text-white'
  const activeLinkText = isOverLightSection ? 'text-black' : 'text-white'
  const activePillBg = isOverLightSection ? 'bg-black/[0.07]' : 'bg-white/[0.1]'

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[100] pointer-events-auto md:pointer-events-none will-change-transform isolate"
      style={{
        transform: navHidden ? 'translateY(-120%)' : 'translateY(0)',
        transition: 'transform 300ms ease-in-out',
      }}
    >
      <div className="pt-3 sm:pt-4 px-5 sm:px-4 md:pointer-events-none">
        {/* Desktop: floating glass pill with logo + nav links only */}
        <div className="hidden md:flex justify-center pointer-events-none">
          <div
            className={`pointer-events-auto inline-flex items-center gap-1.5 rounded-full border backdrop-blur-xl shadow-[0_8px_24px_-8px_rgba(0,0,0,0.4)] transition-colors duration-200 pl-3 pr-2 py-1.5 ${pillBase}`}
          >
            <Link
              to="/"
              onClick={(e) => {
                if (isHomePage) { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }) }
              }}
              className="flex items-center gap-1.5 shrink-0 pr-2.5 mr-1 border-r border-current/10"
            >
              <img src="/Logo_Statbox_Transparent.png" alt="Statbox" className="h-5 w-auto" />
              <img
                src="/logo_white.png"
                alt="Statbox"
                className={`h-2.5 w-auto transition-[filter] duration-300 ${isOverLightSection ? 'invert' : ''}`}
              />
            </Link>

            <ExploreMenu linkText={linkText} activeLinkText={activeLinkText} />

            {!isBookDemo && NAV_SECTIONS.map((item) => {
              const isActive = isHomePage && activeSection === item.id
              return (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`relative rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors ${
                    isActive ? activeLinkText : linkText
                  }`}
                >
                  {isActive && (
                    <motion.span
                      layoutId="nav-active-pill"
                      className={`absolute inset-0 rounded-full ${activePillBg}`}
                      transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                    />
                  )}
                  <span className="relative z-10">{item.name}</span>
                </button>
              )
            })}

            <Link
              to="/docs/api"
              target="_blank"
              rel="noopener noreferrer"
              className={`relative rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors ${linkText}`}
            >
              API Docs
            </Link>

            <Link
              to="/login"
              className="relative z-10 ml-1 inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] font-semibold text-black transition-transform duration-200 hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9]"
              style={{ backgroundColor: '#71cdd9' }}
            >
              Log in
            </Link>
          </div>
        </div>

        {/* Mobile: floating pill with logo + log in */}
        <div className="md:hidden flex justify-center">
          <div
            className={`relative z-10 flex items-center justify-between gap-1 rounded-full border backdrop-blur-md pl-4 pr-1.5 py-1.5 mt-1 mx-auto w-full max-w-[calc(100%-2rem)] touch-manipulation ${
              isOverLightSection
                ? 'bg-black/[0.04] border-black/[0.08]'
                : 'bg-white/[0.05] border-white/10'
            }`}
          >
            <Link
              to="/"
              onClick={(e) => {
                if (isHomePage) { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }) }
              }}
              className="flex items-center gap-1.5 min-h-[40px] touch-manipulation pr-1 shrink-0"
            >
              <img src="/Logo_Statbox_Transparent.png" alt="Statbox" className="h-5 w-auto" />
              <img
                src="/logo_white.png"
                alt="Statbox"
                className={`h-2.5 w-auto transition-[filter] duration-300 ${isOverLightSection ? 'invert' : ''}`}
              />
            </Link>

            <MobileLoginButton />
          </div>
        </div>
      </div>
    </header>
  )
}
